import React, { useState, useRef } from 'react';
import '../styles/Projects.css';
import scanPesoPic from '../assets/scanpesopic.jpg';
import scanPesoVideo from '../assets/scanpesovideo.mp4';
import baseConversionPic from '../assets/baseconversionpic.png';
import pcbLayoutPic from '../assets/pcblayoutpic.jpg';
import setOperationPic from '../assets/setoperationpic.png';
import thesisInventoryVid from '../assets/thesisinventoryvid.mp4';
import theaterPic from '../assets/theaterpic.png';
import humanVoicePic from '../assets/humanvoicepic.jpg';
import ambientPic from '../assets/ambientpic.jpg';
import ambientVideo from '../assets/ambientvid.mp4';
import ambientPoster from '../assets/ambientposter.pdf';
import bcdPic from '../assets/bcdpic.jpg';
import gumballPic from '../assets/gumballpic.jpg';
import labAccessPic from '../assets/labaccesspic.png';
import mealyPic from '../assets/mealypic.png';
import mealyVid from '../assets/mealyvid.mp4';
import smartMedPic from '../assets/smartmedpic.png';
import hungerPic from '../assets/hungerpic.png';
import hungerVid from '../assets/hungervid.mp4';
import picPic3 from '../assets/picpic3.png';
import picPic3Vid from '../assets/picpic3vid.mp4';
import portfolioPic from '../assets/portfoliopic.png';
import portfolioVid from '../assets/portfoliovid.mp4';
import sumobotPic from '../assets/sumobotpic.jpg';
import sumobotVid from '../assets/sumobotvid.mp4';

const projectsData = {
    "1st Year": [
        {
            subject: "CPE-CP2",
            title: "Scan Peso: Smart Philippine Money Recognizer",
            description: "An intelligent currency recognition system that uses YOLOv8 object detection to identify and classify Philippine peso bills and coins in real-time through camera input, providing accessibility for visually impaired users.",
            tools: ["YOLOv8", "Python", "OpenCV", "Deep Learning"],
            role: "Leader",
            contribution: "Handled all coding and development aspects of the project, from dataset preparation to model training and real-time detection implementation.",
            date: "Dec 27, 2024",
            icon: "💰",
            pattern: 1,
            link: null,
            image: scanPesoPic,
            video: scanPesoVideo
        },
        {
            subject: "CPE-CP1",
            title: "Base Number Conversion System: Octal ↔ Binary, Decimal & Hexadecimal",
            description: "A comprehensive number system converter built in C language that handles conversions between Octal, Binary, Decimal, and Hexadecimal bases with bidirectional conversion support and input validation.",
            tools: ["C Language", "Number Systems", "Algorithms"],
            role: "Member",
            contribution: "Contributed to the research paper and assisted in coding the conversion algorithms and testing edge cases.",
            date: "Dec 18, 2023",
            icon: "🔢",
            pattern: 2,
            link: null,
            image: baseConversionPic
        }
    ],
    "2nd Year": [
        {
            subject: "CPE-DES",
            title: "Circuit Design & PCB Layout: Two-Digit Decimal Counter Circuit",
            description: "Designed and simulated a complete two-digit decimal counter circuit, from schematic capture in Multisim to professional PCB layout in Altium Designer, including component selection and signal integrity analysis.",
            tools: ["Multisim", "Altium Designer", "PCB Design", "Circuit Analysis"],
            role: "Leader",
            contribution: "Completed all aspects of the project including circuit design, simulation, PCB layout, and documentation.",
            date: "Jul 6, 2025",
            icon: "⚡",
            pattern: 3,
            link: null,
            image: pcbLayoutPic
        },
        {
            subject: "CPE-DMA",
            title: "GUI-Based Set Operations Application",
            description: "A desktop application with an intuitive graphical user interface for performing mathematical set operations including union, intersection, difference, complement, and symmetric difference with visual Venn diagram representations.",
            tools: ["Python", "PyQt6", "Set Theory", "GUI Design"],
            role: "Leader",
            contribution: "Developed the entire application from UI design to backend logic implementation, handling all coding and testing.",
            date: "May 13, 2025",
            icon: "🔗",
            pattern: 4,
            link: null,
            image: setOperationPic
        },
        {
            subject: "CPE-OOP",
            title: "Thesis Inventory Management System",
            description: "A full-featured database-driven application for managing thesis documents, tracking inventory, searching records, and generating reports. Built with a modern GUI and complete CRUD operations with database integration.",
            tools: ["Python", "PyQt6", "Database", "OOP"],
            role: "Member",
            contribution: "Handled coding, PowerPoint presentation, and research paper documentation for the project.",
            date: "May 18, 2025",
            icon: "📚",
            pattern: 5,
            link: null,
            video: thesisInventoryVid,
            autoplayVideo: true
        },
        {
            subject: "CPE-OOP",
            title: "Movie Theater Seat Booking System",
            description: "An object-oriented Java application simulating a complete movie theater seat booking experience with interactive seat selection, booking management, showtime scheduling, and ticket generation.",
            tools: ["Java", "OOP", "GUI", "Data Structures"],
            role: "Leader",
            contribution: "Led the team as project leader. Handled all coding, paper documentation, and PowerPoint presentation.",
            date: "Apr 12, 2025",
            icon: "🎬",
            pattern: 6,
            link: null,
            image: theaterPic
        },
        {
            subject: "CPE-MAT",
            title: "Human Voice Detection System",
            description: "A signal processing project that detects and analyzes human voice patterns using Python and MATLAB, implementing frequency analysis, noise filtering, and voice activity detection algorithms.",
            tools: ["Python", "MATLAB", "Signal Processing", "DSP"],
            role: "Member",
            contribution: "Contributed to coding the detection algorithms, writing the research paper, and creating the PowerPoint presentation.",
            date: "May 18, 2025",
            icon: "🎙️",
            pattern: 1,
            link: null,
            image: humanVoicePic
        }
    ],
    "3rd Year": [
        {
            subject: "ECEFCS · CPE-ES · CPE-MIC",
            subjects: ["ECEFCS", "CPE-ES", "CPE-MIC"],
            title: "Ambient Light-Based Smart Lighting System",
            description: "A collaborative IoT smart lighting system using an ESP32 microcontroller that automatically adjusts indoor lighting based on ambient light levels. The system reads real-time lux values via an LDR sensor, drives a servo motor for dimmer control, regulates voltage through an SCR voltage regulator, and monitors power consumption with a PZEM monitor — all communicating over HTTP.",
            tools: ["ESP32", "C++", "LDR Sensor", "Servo Motor", "SCR Voltage Regulator", "PZEM Monitor", "HTTP"],
            role: "Member",
            contribution: "Contributed to the full-stack development — from embedded firmware and sensor integration to the HTTP-based web dashboard for real-time monitoring and control. Also helped in writing the IEEE paper and designing the research poster.",
            date: "2026",
            icon: "💡",
            pattern: 4,
            link: null,
            featured: true,
            collaborative: true,
            image: ambientPic,
            video: ambientVideo,
            poster: ambientPoster
        },
        {
            subject: "CPELOG",
            title: "BCD-to-6421 Code Converter: Design, Simulation & Hardware",
            description: "Midterm laboratory project involving the complete design, digital simulation, and physical hardware implementation of a BCD-to-6421 code converter circuit, demonstrating digital logic design principles.",
            tools: ["Digital Logic", "Simulation", "Hardware Prototyping"],
            role: "Member",
            contribution: "Assisted in building and testing the hardware prototype implementation.",
            date: "2025",
            icon: "🔧",
            pattern: 2,
            link: null,
            image: bcdPic
        },
        {
            subject: "CPEMIC1",
            title: "4-Items Gumball Vending Machine Controller with Assertion-Based Verification",
            description: "Designed and verified a digital vending machine controller supporting 4 item selections using assertion-based verification techniques in EDA Playground, ensuring functional correctness through comprehensive testbenches.",
            tools: ["EDA Playground", "SystemVerilog", "ABV", "Digital Design"],
            role: "Leader",
            contribution: "Developed all the HDL code and verification testbenches. Also contributed to the research paper documentation.",
            date: "Nov 17, 2025",
            icon: "🎰",
            pattern: 3,
            link: null,
            image: gumballPic
        },
        {
            subject: "CPE-DS",
            title: "Portfolio Website",
            description: "A modern, responsive portfolio website showcasing academic projects, skills, and achievements. Built with a clean design system featuring smooth animations and interactive elements.",
            tools: ["HTML", "CSS", "JavaScript", "Hosting"],
            role: "Solo Developer",
            contribution: "Designed and developed the entire website from scratch, currently working on deployment and hosting.",
            date: "2026",
            icon: "🌐",
            pattern: 5,
            link: null,
            ongoing: true,
            image: portfolioPic,
            video: portfolioVid
        },
        {
            subject: "CPE-ES",
            title: "Smart & Secure Laboratory Access Pro with Safety Interlock System",
            description: "An embedded system for secure laboratory access control featuring RFID authentication, safety interlocks, environmental monitoring, and emergency protocols — all simulated in Wokwi.",
            tools: ["Wokwi", "Arduino Mega", "Embedded Systems", "IoT"],
            role: "Leader",
            contribution: "Wrote all the embedded code and firmware. Also helped with the research paper and presentation materials.",
            date: "Mar 9, 2026",
            icon: "🔐",
            pattern: 6,
            link: null,
            image: labAccessPic
        },
        {
            subject: "CPEMIC2",
            title: "Mealy Machine-Based Vending Machine Controller (FPGA)",
            description: "Implemented a Mealy finite state machine-based vending machine controller on FPGA hardware, featuring sequential logic design, state optimization, and real-time hardware deployment.",
            tools: ["FPGA", "Verilog/VHDL", "Mealy FSM", "Hardware"],
            role: "Member",
            contribution: "Debugged and optimized existing HDL code. Contributed to the research paper and presentation.",
            date: "Mar 10, 2026",
            icon: "🖥️",
            pattern: 1,
            link: null,
            image: mealyPic,
            video: mealyVid
        },
        {
            subject: "CPE-ES",
            title: "Smart Medicine Cabinet",
            description: "An IoT-enabled smart medicine cabinet simulated in Wokwi using an Arduino Uno R3. Features automated medication reminders, compartment tracking, and real-time monitoring to help users manage prescriptions and dosage schedules effectively.",
            tools: ["Wokwi", "Arduino Uno R3", "Embedded Systems", "IoT"],
            role: "Member",
            contribution: "Served as the coder and schematic designer — developed the embedded firmware and designed the circuit schematics for the cabinet system.",
            date: "2026",
            icon: "💊",
            pattern: 2,
            link: null,
            image: smartMedPic
        },
        {
            subject: "CPEMIC2",
            title: "PIC-PIC 3: A Pico Park FPGA Adaptation",
            description: "A multiplayer mini-game inspired by Pico Park, implemented on FPGA hardware. Supports 4 players using keyboard input with real-time video output to a monitor. Developed using Quartus IDE with custom digital logic for game mechanics, collision detection, and cooperative puzzle-solving gameplay.",
            tools: ["FPGA", "Quartus", "Verilog/VHDL", "Keyboard", "Monitor"],
            role: "Member",
            contribution: "One of the coders — contributed to the game logic design, player movement modules, and hardware integration for real-time display output.",
            date: "2026",
            icon: "🎮",
            pattern: 3,
            link: null,
            image: picPic3,
            video: picPic3Vid
        },
        {
            subject: "CPE-DS",
            title: "HUNGER: Restaurant Web Page with Calorie Tracking",
            description: "A modern restaurant web application featuring a full menu display with detailed calorie counts for each food item. Includes an interactive ordering experience, contact form integration via EmailJS, and real-time data management powered by Firebase.",
            tools: ["React", "EmailJS", "Firebase", "CSS"],
            role: "Member",
            contribution: "Contributed to the frontend development, calorie tracking feature implementation, and Firebase database integration.",
            date: "2026",
            icon: "🍽️",
            pattern: 4,
            link: null,
            image: hungerPic,
            video: hungerVid
        },
        {
            subject: "CPE-ES",
            title: "Sumobot",
            description: "An RC car-style Sumobot built for competitive sumo robot battles. Uses Bluetooth control via a mobile device to maneuver the robot, with motor driver circuitry handling directional movement and speed control.",
            tools: ["Arduino Uno", "L293D Motor Driver IC", "HC-05 Bluetooth Module"],
            role: "Member",
            contribution: "Contributed to the code and prototype assembly of the Sumobot.",
            date: "2026",
            icon: "🤖",
            pattern: 5,
            link: null,
            image: sumobotPic,
            video: sumobotVid
        }
    ]
};

const yearLevels = ["All", "1st Year", "2nd Year", "3rd Year"];

function Projects() {
    const [activeYear, setActiveYear] = useState("All");

    const getFilteredProjects = () => {
        if (activeYear === "All") {
            return Object.entries(projectsData);
        }
        return [[activeYear, projectsData[activeYear] || []]];
    };

    const getTotalCount = (year) => {
        if (year === "All") {
            return Object.values(projectsData).flat().length;
        }
        return (projectsData[year] || []).length;
    };

    const allProjects = Object.values(projectsData).flat();
    const leaderCount = allProjects.filter(p => p.role === "Leader" || p.role === "Solo Developer").length;

    return (
        <section id="projects" className="projects-section">
            <h2 className="section-title">Projects</h2>
            <p className="projects-subtitle">
                A collection of academic projects spanning hardware design, software development, IoT systems, and embedded engineering.
            </p>

            {/* Stats */}
            <div className="projects-stats">
                <div className="project-stat">
                    <span className="project-stat-number">{allProjects.length}</span>
                    <span className="project-stat-label">Total Projects</span>
                </div>
                <div className="project-stat">
                    <span className="project-stat-number">{leaderCount}</span>
                    <span className="project-stat-label">Led as Leader</span>
                </div>
                <div className="project-stat">
                    <span className="project-stat-number">{new Set(allProjects.flatMap(p => p.tools)).size}+</span>
                    <span className="project-stat-label">Technologies</span>
                </div>
                <div className="project-stat">
                    <span className="project-stat-number">3</span>
                    <span className="project-stat-label">Year Levels</span>
                </div>
            </div>

            {/* Year Level Filter Tabs */}
            <div className="year-tabs">
                {yearLevels.map(year => (
                    <button
                        key={year}
                        className={`year-tab ${activeYear === year ? 'active' : ''}`}
                        onClick={() => setActiveYear(year)}
                    >
                        {year}
                        <span className="tab-count">{getTotalCount(year)}</span>
                    </button>
                ))}
            </div>

            {/* Project Cards */}
            {getFilteredProjects().map(([yearName, projects]) => (
                <div key={yearName} className="year-section">
                    <div className="year-label">
                        <span className="year-label-text">{yearName}</span>
                        <div className="year-label-line"></div>
                    </div>

                    <div className="projects-grid">
                        {projects.map((project, idx) => (
                            <div
                                key={`${yearName}-${idx}`}
                                className={`project-card ${project.featured ? 'featured' : ''} ${project.collaborative ? 'collaborative' : ''}`}
                            >
                                {/* Featured Badge */}
                                {project.featured && (
                                    <div className="featured-ribbon">
                                        <span>⭐ Collaborative Project</span>
                                    </div>
                                )}

                                {/* Card Header */}
                                <div className={`card-header ${(project.image || project.autoplayVideo) ? 'has-media' : ''} ${project.image && !project.video && !project.autoplayVideo ? 'no-video' : ''}`}
                                    onMouseEnter={(e) => {
                                        if (project.video && !project.autoplayVideo) {
                                            const vid = e.currentTarget.querySelector('video');
                                            if (vid) { vid.currentTime = 0; vid.play(); }
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        if (project.video && !project.autoplayVideo) {
                                            const vid = e.currentTarget.querySelector('video');
                                            if (vid) { vid.pause(); vid.currentTime = 0; }
                                        }
                                    }}
                                    style={{ cursor: project.video && !project.autoplayVideo ? 'pointer' : 'default' }}
                                >
                                    {project.autoplayVideo ? (
                                        /* Autoplay video only (no image) */
                                        <video
                                            src={project.video}
                                            className="card-header-video autoplay-video"
                                            muted
                                            loop
                                            autoPlay
                                            playsInline
                                            preload="auto"
                                        />
                                    ) : project.image ? (
                                        <>
                                            <img
                                                src={project.image}
                                                alt={project.title}
                                                className="card-header-image"
                                            />
                                            {project.video && (
                                                <video
                                                    src={project.video}
                                                    className="card-header-video"
                                                    muted
                                                    loop
                                                    playsInline
                                                    preload="metadata"
                                                />
                                            )}
                                        </>
                                    ) : (
                                        <>
                                            <div className={`card-header-bg pattern-${project.pattern}`}></div>
                                            <div className="card-header-overlay"></div>
                                            <span className="card-icon">{project.icon}</span>
                                        </>
                                    )}

                                    {/* Collaborative: show multiple subject badges */}
                                    {project.subjects ? (
                                        <div className="subject-badges-group">
                                            {project.subjects.map((subj, si) => (
                                                <span key={si} className="subject-badge collab-badge">{subj}</span>
                                            ))}
                                        </div>
                                    ) : (
                                        <span className="subject-badge">{project.subject}</span>
                                    )}

                                    <span className="date-badge">{project.date}</span>
                                </div>

                                {/* Card Body */}
                                <div className="card-body">
                                    <h3 className="project-title">{project.title}</h3>
                                    <p className="project-description">{project.description}</p>

                                    {/* Tech Tags */}
                                    <div className="tech-stack">
                                        {project.tools.map((tool, i) => (
                                            <span key={i} className={`tech-tag ${project.featured ? 'featured-tag' : ''}`}>{tool}</span>
                                        ))}
                                    </div>

                                    {/* Footer */}
                                    <div className="card-footer">
                                        <span className={`role-badge ${project.role === 'Leader' || project.role === 'Solo Developer' ? 'leader' : 'member'}`}>
                                            {project.role === 'Leader' || project.role === 'Solo Developer' ? '⭐' : '👤'} {project.role}
                                        </span>
                                        <p className="contribution-text">{project.contribution}</p>

                                        <div className="card-footer-links">
                                            {project.link && (
                                                <a
                                                    href={project.link}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className={`card-link ${project.ongoing ? 'ongoing' : ''}`}
                                                >
                                                    {project.ongoing ? 'In Progress' : 'View Project'}
                                                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                                                    </svg>
                                                </a>
                                            )}

                                            {project.poster && (
                                                <a
                                                    href={project.poster}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="card-link poster-link"
                                                >
                                                    📄 Research Poster
                                                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                    </svg>
                                                </a>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            ))}
        </section>
    );
}

export default Projects;
